import { Box, Col, Link, Text } from '@opendoor/bricks-next';
import type { EntryComponent } from 'cms/entries/entries';
import type { IFcContactUs } from 'declarations/contentful';

import { FCContentContainer, FCContentContainerWrapper } from './shared/FCLayout';

const ContactButton = ({
  label,
  href,
  analyticsName,
}: {
  label: string;
  href: string;
  analyticsName: string;
}) => (
  <Link
    href={href}
    analyticsName={analyticsName}
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    backgroundColor="$backgroundPrimary"
    borderRadius="$4x"
    paddingHorizontal="$8x"
    minHeight={40}
    minWidth={176}
    textDecorationLine="none"
    $smallerThanMD={{ width: '100%', maxWidth: '$240x' }}
    $largerThanSM={{ minHeight: 50, minWidth: 220, borderRadius: '$6x' }}
  >
    <Text
      tag="span"
      typography="$bodySmall"
      fontWeight="$medium"
      color="$contentPrimary"
      $largerThanSM={{ typography: '$bodyLarge' }}
    >
      {label}
    </Text>
  </Link>
);

const DEFAULT_HEADING = 'Questions? We’re here.';
const DEFAULT_SUBTITLE =
  'Tell us about your situation. We’ll help you figure out the best path forward.';

const DEFAULT_BUTTONS = [
  {
    label: 'Visit our help center',
    href: 'https://help.opendoor.com/',
    analyticsName: 'cosmos-fc-contact-us-help-center',
  },
];

const RenderFCContactUs = (entry: IFcContactUs) => {
  const { fields } = entry;
  const heading = fields?.heading || DEFAULT_HEADING;
  const subtitle = fields?.subtitle || DEFAULT_SUBTITLE;
  const buttons = fields?.contactButtons?.length
    ? fields.contactButtons.map(({ fields: { text, href, analyticsId } }) => ({
        label: text,
        href,
        analyticsName: `cosmos-fc-contact-us-${analyticsId}`,
      }))
    : DEFAULT_BUTTONS;

  return (
    <FCContentContainerWrapper style={{ backgroundColor: '#ECE9E7' }}>
      <FCContentContainer py="$40x">
        <Col alignItems="center" justifyContent="center" $largerThanSM={{ gap: '$24x' }} gap="$12x">
          <Col $largerThanSM={{ gap: '$12x' }} gap="$4x">
            <Text
              tag="h2"
              typography="$labelXlarge"
              textAlign="center"
              color="$contentPrimary"
              $largerThanSM={{ typography: '$headerSmall' }}
            >
              {heading}
            </Text>
            <Text
              tag="p"
              typography="$bodySmall"
              color="$contentPrimary"
              textAlign="center"
              maxWidth={663}
              $largerThanSM={{ typography: '$bodyXlarge' }}
            >
              {subtitle}
            </Text>
          </Col>
          <Box
            flexDirection="column"
            alignItems="center"
            gap="$6x"
            $largerThanSM={{
              flexDirection: 'row',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '$24x',
            }}
            width="100%"
          >
            {buttons.map((button) => (
              <ContactButton
                key={button.analyticsName}
                label={button.label}
                href={button.href}
                analyticsName={button.analyticsName}
              />
            ))}
          </Box>
        </Col>
      </FCContentContainer>
    </FCContentContainerWrapper>
  );
};

const FCContactUs: EntryComponent<IFcContactUs> = {
  render: RenderFCContactUs,
};

export default FCContactUs;
